import { FormQuestionContentDetailType } from '@/enums/FormTemplate';
import QUESTION_IMAGE_POSITION from '@/types/question-image_position';
import QUESTION_OPTIONS_LAYOUT from '@/types/question-options_layout';
import OmniRiskProfilesQuestionnaire, {
  OmniRiskProfilesQuestionnaireOption,
  OmniRiskProfilesQuestionnaireQuestion,
  OmniRiskProfilesQuestionnaireQuestionContent,
  OmniRiskProfilesQuestionnaireSection,
} from '@/types/risk-profiles/omni/omni-risk-profiles-questionnaire';
import ProposalOmniRiskProfilesQuestionnaire, {
  ProposalOmniRiskProfilesQuestionnaireOption,
  ProposalOmniRiskProfilesQuestionnaireQuestion,
  ProposalOmniRiskProfilesQuestionnaireSection,
} from '@/types/risk-profiles/omni/proposal-omni-risk-profiles-questionnaire';
import { LOCALE } from './i18n';
import Dictionary from '@/types/dictionary';
import PrintOptions from '@/types/print-options';

// Local types. Move if needed more globally.
type ImageData = {
  width: number;
  height: number;
  url?: string;
};

type PrintOptionsPerOrientation = {
  landscape: PrintOptions;
  portrait: PrintOptions;
};

type PrintOptionsWithImgDimensions = PrintOptions & {
  imgDimensions: {
    width: number | string;
    height: number | string;
  };
};

// Utility function to merge printOptions with defaults
const mergePrintOptionsWithDefaults = (printOptions?: PrintOptionsPerOrientation, imageData?: ImageData) => {
  if (!imageData) return undefined;

  const MAX_WIDTH_ALLOWED = 450;
  const hasReachedMaxWidth = imageData.width > MAX_WIDTH_ALLOWED;

  const createDefaults = (pageFraction: number) => ({
    imgDimensions: {
      height: hasReachedMaxWidth ? 'auto' : imageData.height,
      width: hasReachedMaxWidth ? MAX_WIDTH_ALLOWED : imageData.width,
    },
    imgPosition: QUESTION_IMAGE_POSITION.LEFT,
    optionsLayout: QUESTION_OPTIONS_LAYOUT.STACKED,
    pageFraction,
  });

  const mergeOptions = (orientation: 'landscape' | 'portrait', defaults: PrintOptionsWithImgDimensions) => ({
    imgDimensions: {
      height: printOptions?.[orientation]?.imgDimensions?.height ?? defaults.imgDimensions.height,
      width: printOptions?.[orientation]?.imgDimensions?.width ?? defaults.imgDimensions.width,
    },
    imgPosition: printOptions?.[orientation]?.imgPosition ?? defaults.imgPosition,
    optionsLayout: printOptions?.[orientation]?.optionsLayout ?? defaults.optionsLayout,
    pageFraction: printOptions?.[orientation]?.pageFraction ?? defaults.pageFraction,
  });

  const landscapeDefaults = createDefaults(1);
  const portraitDefaults = createDefaults(0.5);

  return {
    landscape: mergeOptions('landscape', landscapeDefaults),
    portrait: mergeOptions('portrait', portraitDefaults),
  };
};

// Transform response by adding _id in sections data and printOptions to be aligned with the expected structure in the proposal engine
export const transformQuestionnaire = (questionnaire: OmniRiskProfilesQuestionnaire): ProposalOmniRiskProfilesQuestionnaire => {
  // Transform image to have default values for printOptions
  const transformImage = (content?: OmniRiskProfilesQuestionnaireQuestionContent[], printOptions?: PrintOptionsPerOrientation) => {
    const image = content?.find((c: OmniRiskProfilesQuestionnaireQuestionContent) => c.type === FormQuestionContentDetailType.IMAGE);
    if (!image || !image.value[LOCALE.EN_CA]) return {};

    const mergedPrintOptions = mergePrintOptionsWithDefaults(printOptions, image.value[LOCALE.EN_CA]);

    return {
      description: {
        imgUrl: {
          [LOCALE.EN_CA]: image.value[LOCALE.EN_CA]?.url,
          [LOCALE.FR_CA]: image.value[LOCALE.FR_CA]?.url,
        },
      },
      ...(mergedPrintOptions && { printOptions: mergedPrintOptions }),
    };
  };

  const transformOption = (option: OmniRiskProfilesQuestionnaireOption): ProposalOmniRiskProfilesQuestionnaireOption => ({
    ...option,
    _id: option.omniOptionId,
  });

  const transformQuestion = (question: OmniRiskProfilesQuestionnaireQuestion): ProposalOmniRiskProfilesQuestionnaireQuestion => {
    const hasContent = question.content && question.content?.length > 0;
    return {
      ...question,
      _id: question.omniQuestionId,
      options: question.options.map(transformOption) || [],
      ...(hasContent && question.content?.some((c: OmniRiskProfilesQuestionnaireQuestionContent) => c.type === FormQuestionContentDetailType.IMAGE)
        ? transformImage(question.content, question.printOptions)
        : {}),
    };
  };

  const transformSection = (section: OmniRiskProfilesQuestionnaireSection): ProposalOmniRiskProfilesQuestionnaireSection => {
    const hasTitle = section.title?.[LOCALE.EN_CA];
    const title = hasTitle ? (section.title as Dictionary<string>) : { [LOCALE.EN_CA]: '', [LOCALE.FR_CA]: '' };
    return {
      ...section,
      _id: section.omniSectionId,
      questions: section.questions.map(transformQuestion) || [],
      title,
    };
  };

  return {
    ...questionnaire,
    sections: questionnaire.sections.map(transformSection) || [],
  };
};
